import React, {
  FunctionComponent as FC,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { actionCreators } from "../../../../state";
import { State } from "../../../../state";
import "./index.scss";
import type { RadioChangeEvent } from 'antd';
import { Spin, Slider, Switch, Col, Row, Radio, ConfigProvider, Select } from "antd";
import _ from "lodash";

import Panel from "../../common/Panel";
import { viewSize, themeColor, piViewStyle } from "../../../../types/style.global";
import { GlobalStyle } from "../../../../types/style.d";

import DonutAndRadarChart from "../../common/Visualization/DonutAndRadarChart";
import SubTitle from "../../common/SubTitle";
import { PIViewTypes, GrantViewTypes, ImpactViewTypes } from "../../../../types/data";
import DonutChart from "../../common/Visualization/DonutChart";
import RadarChart from "../../common/Visualization/RadarChart";
import BarChart from "../../common/Visualization/BarChart";
import AreaChart from "../../common/Visualization/AreaChart";
import PICard from "../../common/Visualization/PICard";

import { FixedSizeList } from 'react-window';
import { CitationFlowType, PICardSortMetric } from "../../../../types/data.global";
import DownloadButton from "../../common/DownloadButton";

interface PIViewProps {
  viewParam: GlobalStyle.ViewSize;
  panelParam: GlobalStyle.PanelStyle;
  viewName: string;
}

interface SelectOptionFormat {
  value: string;
  label: string;
}

const PIView: FC<PIViewProps> = (props) => {
  const { viewParam, panelParam, viewName } = props;

  const dispatch = useDispatch()
  const {
    getPIViewData,
    getPIViewCardListData,
    getPIViewPredictedCardListData,
    updatePIViewLoading,
  } = bindActionCreators(actionCreators, dispatch)

  // data reducer
  const grantIDList: string[] = useSelector((state: State) => state.dataReducer?.grantIDList) as string[]
  const predictedGrantIDList: string[] = useSelector((state: State) => state.dataReducer?.predictedGrantIDList) as string[]
  const predictedGrantIDListGlobal: string[] = useSelector((state: State) => state.dataReducer?.predictedGrantIDListGlobal) as string[]
  const piViewGenderDonutChartDataOuter: PIViewTypes.DonutChartDataUnit[] = useSelector((state: State) => state.dataReducer?.piViewGenderDonutChartDataOuter) as PIViewTypes.DonutChartDataUnit[]
  const piViewGenderDonutChartDataInner: PIViewTypes.DonutChartDataUnit[] = useSelector((state: State) => state.dataReducer?.piViewGenderDonutChartDataInner) as PIViewTypes.DonutChartDataUnit[]
  const piViewGenderRadarChartData: PIViewTypes.RadarChartEntity[] = useSelector((state: State) => state.dataReducer?.piViewGenderRadarChartData) as PIViewTypes.RadarChartEntity[]
  const piViewCareerAgeDonutChartDataOuter: PIViewTypes.DonutChartDataUnit[] = useSelector((state: State) => state.dataReducer?.piViewCareerAgeDonutChartDataOuter) as PIViewTypes.DonutChartDataUnit[]
  const piViewCareerAgeDonutChartDataInner: PIViewTypes.DonutChartDataUnit[] = useSelector((state: State) => state.dataReducer?.piViewCareerAgeDonutChartDataInner) as PIViewTypes.DonutChartDataUnit[]
  const piViewCareerAgeRadarChartData: PIViewTypes.RadarChartEntity[] = useSelector((state: State) => state.dataReducer?.piViewCareerAgeRadarChartData) as PIViewTypes.RadarChartEntity[]
  const piViewUniversityBarChartData: GrantViewTypes.BarChartDataUnit[] = useSelector((state: State) => state.dataReducer?.piViewUniversityBarChartData) as GrantViewTypes.BarChartDataUnit[]
  const piViewSciFieldBarChartData: GrantViewTypes.BarChartDataUnit[] = useSelector((state: State) => state.dataReducer?.piViewSciFieldBarChartData) as GrantViewTypes.BarChartDataUnit[]
  const piViewHIndexAreaChartData: ImpactViewTypes.AreaChartDataUnit[] = useSelector((state: State) => state.dataReducer?.piViewHIndexAreaChartData) as ImpactViewTypes.AreaChartDataUnit[]
  const piViewProductivityAreaChartData: ImpactViewTypes.AreaChartDataUnit[] = useSelector((state: State) => state.dataReducer?.piViewProductivityAreaChartData) as ImpactViewTypes.AreaChartDataUnit[]
  const piViewCardListData: PIViewTypes.PIStatisticsUnit[] = useSelector((state: State) => state.dataReducer?.piViewCardListData) as PIViewTypes.PIStatisticsUnit[]
  const piViewPredictedCardListData: PIViewTypes.PIStatisticsUnit[] = useSelector((state: State) => state.dataReducer?.piViewPredictedCardListData) as PIViewTypes.PIStatisticsUnit[]
  const isPIViewLoading: boolean = useSelector((state: State) => state.dataReducer?.isPIViewLoading) as boolean
  const citationFlowType: string = useSelector((state: State) => state.uiReducer?.citationFlowType) as string


  // state variables
  const [radioValue, setRadioValue] = useState('Statistics');
  const [isPIStatisticsDisplay, setPIStatisticsDisplay] = useState("block");
  const [isPIListDisplay, setPIListDisplay] = useState("none");
  const [selectedResearcherID, setSelectedResearcherID] = useState<string>('')
  const [boxShadowStyle, setBoxShadowStyle] = useState<string>('')
  // rank PI Card list by field
  let [rankByField, setRankByField] = useState(PICardSortMetric.PRODUCTIVITY);
  let [rankOrder, setRankOrder] = useState('descending');


  // Manage concurrent async calls so Spin stays on until all finish
  const pendingPIRequestsRef = useRef(0);
  const startLoading = () => {
    if (pendingPIRequestsRef.current === 0) {
      updatePIViewLoading(true);
    }
    pendingPIRequestsRef.current += 1;
  };
  const endLoading = () => {
    pendingPIRequestsRef.current = Math.max(0, pendingPIRequestsRef.current - 1);
    if (pendingPIRequestsRef.current === 0) {
      updatePIViewLoading(false);
    }
  };


  // 1. function to updatePIView: call backend to update PI view
  /*
    Description:
      Update the PI view data.
      Triggered anytime when the grantIDList is updated.
    Input Parameters:
      `selectedGrantIDList`: list[str]
    Output:
      xxx
  */
  const updatePIView = async (selectedGrantIDList: string[]) => {
    startLoading()
    try {
      // const selectedUniversity = "all"
      const result = await getPIViewData(selectedGrantIDList)
      // const cardListData = await getPIViewCardListData(selectedGrantIDList, selectedUniversity)
    } catch (error) {
      console.error(error);
    } finally {
      endLoading()
    }
  }
  const updatePICardListView = async (selectedUniversity: string = "all") => {
    startLoading()
    try {
      const cardListData = await getPIViewCardListData(grantIDList, selectedUniversity, rankOrder)
    } catch (error) {
      console.error(error);
    } finally {
      endLoading()
    }
  }
  const updatePredictedPICardListView = async (selectedUniversity: string= "all") => {
    startLoading()
    try {
      // console.log('==> predictedGrantIDList: ', predictedGrantIDList)
      // console.log('==> predictedGrantIDListGlobal: ', predictedGrantIDListGlobal)
      const predictedCardListData = await getPIViewPredictedCardListData(predictedGrantIDList, predictedGrantIDListGlobal, selectedUniversity, rankOrder)
    } catch (error) {
      console.error(error);
    } finally {
      endLoading()
    }
  }

  useEffect(() => {
    // if(grantIDList.length > 0){
    //   updatePIView()
    // }
    updatePIView(grantIDList)
    updatePICardListView()
  }, [dispatch, grantIDList]);

  useEffect(() => {
    // console.log('\n\n\n==> PIView() > useEffect() > predictedGrantIDList: ')
    // console.log('==> PIView() > predictedGrantIDList: ', predictedGrantIDList.length, predictedGrantIDList)
    // console.log('==> PIView() > predictedGrantIDListGlobal: ', predictedGrantIDListGlobal.length, predictedGrantIDListGlobal)
    updatePIView(predictedGrantIDList)
    updatePredictedPICardListView()
  }, [predictedGrantIDList]);




  // 2. handle selecting a PI Card: call backend to update all views
  // TODO (20250706): to update the grantIDList from backend by filtering the grants by the PI name. Then update the main view and right two views.


  // 3. post-processing: PI Card List: handle sorting
  const sortedResearcherList = useMemo(() => {
    let list: PIViewTypes.PIStatisticsUnit[] = []
    if(citationFlowType === CitationFlowType.PREDICTION && piViewPredictedCardListData.length > 0){
      list = piViewPredictedCardListData.slice()
    }
    else{
      list = piViewCardListData.slice()
    }

    if(rankByField === PICardSortMetric.PSCORE){
      list = list.sort(function(a, b){
        if(rankOrder === 'ascending'){
          return (b.scisciIndex.pScore < a.scisciIndex.pScore) ? 1 : (b.scisciIndex.pScore > a.scisciIndex.pScore)? -1 : 0;
        }
        else{
          // descending
          return (b.scisciIndex.pScore > a.scisciIndex.pScore) ? 1 : (b.scisciIndex.pScore < a.scisciIndex.pScore)? -1 : 0;
        }
      })
    }
    else if(rankByField === PICardSortMetric.TOTALGRANTS){
      list = list.sort(function(a, b){
        if(rankOrder === 'ascending'){
          return (b.scisciIndex.totalGrants < a.scisciIndex.totalGrants) ? 1 : (b.scisciIndex.totalGrants > a.scisciIndex.totalGrants)? -1 : 0;
        }
        else{
          // descending
          return (b.scisciIndex.totalGrants > a.scisciIndex.totalGrants) ? 1 : (b.scisciIndex.totalGrants < a.scisciIndex.totalGrants)? -1 : 0;
        }
      })
    }
    else if(rankByField === PICardSortMetric.TOTALPUBLICATIONS){
      list = list.sort(function(a, b){
        if(rankOrder === 'ascending'){
          return (b.scisciIndex.totalPublications < a.scisciIndex.totalPublications) ? 1 : (b.scisciIndex.totalPublications > a.scisciIndex.totalPublications)? -1 : 0;
        }
        else{
          // descending
          return (b.scisciIndex.totalPublications > a.scisciIndex.totalPublications) ? 1 : (b.scisciIndex.totalPublications < a.scisciIndex.totalPublications)? -1 : 0;
        }
      })
    }
    else if(rankByField === PICardSortMetric.HINDEX){
      list = list.sort(function(a, b){
        if(rankOrder === 'ascending'){
          return (b.scisciIndex.hIndex < a.scisciIndex.hIndex) ? 1 : (b.scisciIndex.hIndex > a.scisciIndex.hIndex)? -1 : 0;
        }
        else{
          // descending
          return (b.scisciIndex.hIndex > a.scisciIndex.hIndex) ? 1 : (b.scisciIndex.hIndex < a.scisciIndex.hIndex)? -1 : 0;
        }
      })
    }
    else if(rankByField === 'productivity'){
      list = list.sort(function(a, b){
        if(rankOrder === 'ascending'){
          return (b.scisciIndex.productivity < a.scisciIndex.productivity) ? 1 : (b.scisciIndex.productivity > a.scisciIndex.productivity)? -1 : 0;
        }
        else{
          // descending
          return (b.scisciIndex.productivity > a.scisciIndex.productivity) ? 1 : (b.scisciIndex.productivity < a.scisciIndex.productivity)? -1 : 0;
        }
      })
    }
    else{
      //rankByField === 'productivity'
      list = list.sort(function(a, b){
        if(rankOrder === 'ascending'){
          return (b.scisciIndex.productivity < a.scisciIndex.productivity) ? 1 : (b.scisciIndex.productivity > a.scisciIndex.productivity)? -1 : 0;
        }
        else{
          // descending
          return (b.scisciIndex.productivity > a.scisciIndex.productivity) ? 1 : (b.scisciIndex.productivity < a.scisciIndex.productivity)? -1 : 0;
        }
      })
    }

    return list;
  }, [citationFlowType, piViewPredictedCardListData, piViewCardListData, rankByField, rankOrder]);

  // let list: PIViewTypes.PIStatisticsUnit[] = []
  // if(citationFlowType === CitationFlowType.PREDICTION && piViewPredictedCardListData.length > 0){
  //   list = piViewPredictedCardListData.slice()
  // }
  // else{
  //   list = piViewCardListData.slice()
  // }
  // if(rankByField === PICardSortMetric.PSCORE){
  //   list = list.sort(function(a, b){
  //     if(rankOrder === 'ascending'){
  //       return (b.scisciIndex.pScore < a.scisciIndex.pScore) ? 1 : (b.scisciIndex.pScore > a.scisciIndex.
  //         pScore)? -1 : 0;
  //     }
  //     else{
  //       // descending
  //       return (b.scisciIndex.pScore > a.scisciIndex.pScore) ? 1 : (b.scisciIndex.pScore < a.scisciIndex.
  //         pScore)? -1 : 0;
  //     }
  //   })
  // }
  // else if(rankByField === PICardSortMetric.TOTALGRANTS){
  //   list = list.sort(function(a, b){
  //     if(rankOrder === 'ascending'){
  //       return (b.scisciIndex.totalGrants < a.scisciIndex.totalGrants) ? 1 : (b.scisciIndex.totalGrants > a.
  //         scisciIndex.totalGrants)? -1 : 0;
  //     }
  //     else{
  //       // descending
  //       return (b.scisciIndex.totalGrants > a.scisciIndex.totalGrants) ? 1 : (b.scisciIndex.totalGrants < a.
  //         scisciIndex.totalGrants)? -1 : 0;
  //     }
  //   })
  // }
  // else if(rankByField === PICardSortMetric.TOTALPUBLICATIONS){
  //   list = list.sort(function(a, b){
  //     if(rankOrder === 'ascending'){
  //       return (b.scisciIndex.totalPublications < a.scisciIndex.totalPublications) ? 1 : (b.scisciIndex.
  //         totalPublications > a.scisciIndex.totalPublications)? -1 : 0;
  //     }
  //     else{
  //       // descending
  //       return (b.scisciIndex.totalPublications > a.scisciIndex.totalPublications) ? 1 : (b.scisciIndex.
  //         totalPublications < a.scisciIndex.totalPublications)? -1 : 0;
  //     }
  //   })
  // }
  // else if(rankByField === PICardSortMetric.HINDEX){
  //   list = list.sort(function(a, b){
  //     if(rankOrder === 'ascending'){
  //       return (b.scisciIndex.hIndex < a.scisciIndex.hIndex) ? 1 : (b.scisciIndex.hIndex > a.scisciIndex.
  //         hIndex)? -1 : 0;
  //     }
  //     else{
  //       // descending
  //       return (b.scisciIndex.hIndex > a.scisciIndex.hIndex) ? 1 : (b.scisciIndex.hIndex < a.scisciIndex.
  //         hIndex)? -1 : 0;
  //     }
  //   })
  // }
  // else if(rankByField === 'productivity'){
  //   list = list.sort(function(a, b){
  //     if(rankOrder === 'ascending'){
  //       return (b.scisciIndex.productivity < a.scisciIndex.productivity) ? 1 : (b.scisciIndex.productivity > a.
  //         scisciIndex.productivity)? -1 : 0;
  //     }
  //     else{
  //       // descending
  //       return (b.scisciIndex.productivity > a.scisciIndex.productivity) ? 1 : (b.scisciIndex.productivity < a.
  //         scisciIndex.productivity)? -1 : 0;
  //     }
  //   })
  // }
  // else{
  //   //rankByField === 'productivity'
  //   list = list.sort(function(a, b){
  //     if(rankOrder === 'ascending'){
  //       return (b.scisciIndex.productivity < a.scisciIndex.productivity) ? 1 : (b.scisciIndex.productivity > a.
  //         scisciIndex.productivity)? -1 : 0;
  //     }
  //     else{
  //       // descending
  //       return (b.scisciIndex.productivity > a.scisciIndex.productivity) ? 1 : (b.scisciIndex.productivity < a.
  //         scisciIndex.productivity)? -1 : 0;
  //     }
  //   })
  // }
  // console.log("list:", list)
  // const [selectedPICardListData, setSelectedPICardListData] = useState(list);

  // console.log("sortedResearcherList:", sortedResearcherList)
  const [selectedUniversity, setSelectedUniversity] = useState<string>("all");

  // 使用 useMemo 缓存过滤后的数据，避免额外的重新渲染
  const selectedPICardListData = useMemo(() => {
    if (selectedUniversity === "all") {
      return sortedResearcherList;
    } else {
      return sortedResearcherList.filter((item: PIViewTypes.PIStatisticsUnit) =>
        item.demographicInfo.organizationName === selectedUniversity
      );
    }
  }, [sortedResearcherList, selectedUniversity]);

  // 4. event handlers
  const onRadioChange = useCallback(({ target: { value } }: RadioChangeEvent) => {
  // const onRadioChange = ({ target: { value } }: RadioChangeEvent) => {
    console.log('radio checked', value);
    setRadioValue(value);
    if(value === 'Statistics') {
      setPIStatisticsDisplay("block");
      setPIListDisplay("none");
    }
    else if(value === 'PI List') {
      setPIStatisticsDisplay("none");
      setPIListDisplay("block");
    }
  }, []);
  const handleSelectChange = useCallback((value: string) => {
    console.log(`selected ${value}`);
    setRankByField(value as PICardSortMetric);
  }, []);
  const handleSelectOrderChange = useCallback((value: string) => {
    console.log(`selected order ${value}`);
    setRankOrder(value)
  }, []);

  const handleSelectUniversityChange = useCallback((value: string) => {
    console.log(`selected university ${value}`);
    setSelectedUniversity(value);
    // 移除后端调用，因为现在使用前端过滤
    // updatePICardListView(value);
  }, []);

  const handleSelectUniversitySearch = useCallback((value: string) => {
    console.log('handleSelectUniversitySearch:', value);
    // 搜索时也更新选中的university
    setSelectedUniversity(value);
  }, []);








  // Filter `option.label` match the user type `input`
  const handleSelectUniversityfilterOption = useCallback((input: string, option?: { label: string; value: string }) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase()), []);

  const handleOnClickPICard = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const id: string = event.currentTarget.id
    const dataList: string[] = id.split('-')
    const fullName: string = dataList[0]
    const researcherID: string = dataList[1]
    console.log('click researcher: ', fullName)

    // highlight this card
    if (researcherID !== selectedResearcherID) { // 选中
      const bosShadow = '0 4px 10px 0 '
      setBoxShadowStyle(bosShadow + themeColor.defaultMode.selectionColor.highlight)
      setSelectedResearcherID(researcherID)
    } else { // 取消选中, mainview去获取默认全体数据
      //取消选中, mainview去获取选中card前的researcher id list
      setBoxShadowStyle('')
      setSelectedResearcherID('')
    }
  }, [selectedResearcherID, themeColor.defaultMode.selectionColor.highlight])


  // 5. visualization
  // Responsive visualization size
  // 1. radar and donut chart
  const piViewRef = useRef<HTMLDivElement>(null);
  const radarAndDonutChartParentDivRef = useRef<HTMLDivElement>(null);
  const [radarAndDonutChartParentDivSize, setRadarAndDonutChartParentDivSize] = useState({ width: 0, height: 0 });
  const [radarChartDiameter, setRadarChartDiameter] = useState(0);
  const [donutChartDiameter, setDonutChartDiameter] = useState(0);
  // const [radarChartWidth, setRadarChartWidth] = useState(0);
  // const [radarChartHeight, setRadarChartHeight] = useState(0);
  const [barAndAreaChartWidth, setBarAndAreaChartWidth] = useState(0);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!radarAndDonutChartParentDivRef.current) return;

    const handleResize = (entries: ResizeObserverEntry[]) => {
      // Clear any existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Debounce the resize handling
      resizeTimeoutRef.current = setTimeout(() => {
        if (entries.length === 0) return;

        const entry = entries[0];
        const cr = entry.contentRect;
        const newWidth = cr.width * 0.95;
        const newHeight = cr.height * 0.95;

        // Only update if the size actually changed significantly (avoid micro-changes)
        const threshold = 1; // 1px threshold
        const currentSize = radarAndDonutChartParentDivSize;

        if (Math.abs(newWidth - currentSize.width) > threshold ||
            Math.abs(newHeight - currentSize.height) > threshold) {

          // Use requestAnimationFrame to batch DOM updates
          requestAnimationFrame(() => {
            setRadarAndDonutChartParentDivSize({ width: newWidth, height: newHeight });
            const minDiameter = Math.min(newWidth, newHeight)
            setRadarChartDiameter(minDiameter * (0.24/0.35))
            setDonutChartDiameter(minDiameter)
            // setRadarChartDiameter(minDiameter * 0.24)
            // setDonutChartDiameter(minDiameter * 0.35)
            setBarAndAreaChartWidth(Math.max(0, cr.width * 2));
          });
        }
      }, 16); // ~60fps throttling
    };

    // Handle ResizeObserver loop errors
    const ro = new ResizeObserver((entries) => {
      try {
        handleResize(entries);
      } catch (error) {
        // Suppress ResizeObserver loop errors - these are usually benign
        if (error instanceof Error && error.message.includes('ResizeObserver loop')) {
          console.warn('ResizeObserver loop detected, suppressing error');
          return;
        }
        console.error('ResizeObserver error:', error);
      }
    });

    ro.observe(radarAndDonutChartParentDivRef.current);

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      ro.disconnect();
    };
  }, [radarAndDonutChartParentDivSize.width, radarAndDonutChartParentDivSize.height]);

  useEffect(() => {
    if (!radarAndDonutChartParentDivRef.current) return;

    const handleResize = (entries: ResizeObserverEntry[]) => {
      // Clear any existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Debounce the resize handling
      resizeTimeoutRef.current = setTimeout(() => {
        if (entries.length === 0) return;

        const entry = entries[0];
        const cr = entry.contentRect;
        const newWidth = cr.width * 0.95;
        const newHeight = cr.height * 0.95;

        // Only update if the size actually changed significantly (avoid micro-changes)
        const threshold = 1; // 1px threshold
        const currentSize = radarAndDonutChartParentDivSize;

        if (Math.abs(newWidth - currentSize.width) > threshold ||
            Math.abs(newHeight - currentSize.height) > threshold) {

          // Use requestAnimationFrame to batch DOM updates
          requestAnimationFrame(() => {
            setRadarAndDonutChartParentDivSize({ width: newWidth, height: newHeight });
            const minDiameter = Math.min(newWidth, newHeight)
            setRadarChartDiameter(minDiameter * (0.24/0.35))
            setDonutChartDiameter(minDiameter)
            // setRadarChartDiameter(minDiameter * 0.24)
            // setDonutChartDiameter(minDiameter * 0.35)
            setBarAndAreaChartWidth(Math.max(0, cr.width * 2));
          });
        }
      }, 16); // ~60fps throttling
    };

    // Handle ResizeObserver loop errors
    const ro = new ResizeObserver((entries) => {
      try {
        handleResize(entries);
      } catch (error) {
        // Suppress ResizeObserver loop errors - these are usually benign
        if (error instanceof Error && error.message.includes('ResizeObserver loop')) {
          console.warn('ResizeObserver loop detected, suppressing error');
          return;
        }
        console.error('ResizeObserver error:', error);
      }
    });

    ro.observe(radarAndDonutChartParentDivRef.current);

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      ro.disconnect();
    };
  }, [radarAndDonutChartParentDivSize.width, radarAndDonutChartParentDivSize.height]);

  // console.log('barAndAreaChartWidth:', barAndAreaChartWidth);

  // 2. PI Card List
  const cardListContainerRef = useRef<HTMLDivElement>(null);
  const [cardListWidth, setCardListWidth] = useState(0);
  useEffect(() => {
    if (cardListContainerRef.current) {
      const width = cardListContainerRef.current.clientWidth;
      setCardListWidth(width * 0.95);
    }
  }, [cardListContainerRef]);
  // console.log('cardListWidth:', cardListWidth);


  const colorMapGender = piViewStyle.colorMapGender as Record<string, string>
  const colorMapCareerAge = piViewStyle.colorMapCareerAge as Record<string, string>

  // BarChart
  // const barAndAreaChartWidth = piViewStyle.barChart.width
  const barChartHeight = piViewStyle.barChart.height
  let universityBarChart: JSX.Element = <></>
  let sciFieldBarChart: JSX.Element = <></>
  universityBarChart = <BarChart width={barAndAreaChartWidth} height={barChartHeight} heightWithLabel={piViewStyle.barChart.height + piViewStyle.barChart.labelHeight + piViewStyle.barChart.labelMarginBottom} chartTitle="University" data={piViewUniversityBarChartData} />
  sciFieldBarChart = <BarChart width={barAndAreaChartWidth} height={barChartHeight} heightWithLabel={piViewStyle.barChart.height + piViewStyle.barChart.labelMarginBottom} chartTitle="Science_field" data={piViewSciFieldBarChartData} />

  // AreaChart
  // const areaChartWidth = piViewStyle.areaChart.width;
  const areaChartHeight = piViewStyle.areaChart.height;
  const areaChartColor = [themeColor.defaultMode.barChartBarColor]

  // 将University Barchart的数据转换为PI Card List中select框的Option的数据
  const universityOptionData: SelectOptionFormat[] = useMemo(() => [
    { value: "all", label: "All Universities" },
    ...piViewUniversityBarChartData.map(item => {
      // 获取当前对象的键名（属性名），假设每个对象只有一个键
      const key = Object.keys(item)[0];
      // 根据键名创建新的对象
      return {
        value: key,
        label: key,
      };
    })
  ], [piViewUniversityBarChartData]);
  // const universityOptionData: SelectOptionFormat[] = piViewUniversityBarChartData.map(item => {
  //   // 获取当前对象的键名（属性名），假设每个对象只有一个键
  //   const key = Object.keys(item)[0];
  //   // 根据键名创建新的对象
  //   return {
  //     value: key,
  //     label: key,
  //   };
  // });


  const handleDownload = () => {
    console.log('download pi meta table');
    // TODO: download pi meta table
  }

  // 3. PI Card List
  const PICardElemementList = useMemo(() => selectedPICardListData.map((researcherData, index) => {
    return (
      <div key={index}>
        <div
          id={`${researcherData.demographicInfo.fullName}-${researcherData.researcherID}-pi-card`}
          // style={{ boxShadow: boxShadowStyle }}
          onClick={(e)=>handleOnClickPICard(e)}
        >
          <PICard
            viewParam={viewSize.PIStatisticsViewCard}
            PIStatisticsData={researcherData}
            // boxShadowStyle={currBoxShadowStyle}
            // divOutlineColor={divOutlineColor}
          />
        </div>
      </div>
    )
  }), [selectedPICardListData, handleOnClickPICard])


  return (
    <div className="pi-view-layout-container"> {/* 添加一个新的外层容器，用于定位 */}
    <Panel
      viewTitle={viewName}
      width={viewParam.width}
      height={viewParam.height}
      margin={panelParam.margin}
      padding={panelParam.padding}
      panelHeaderHeight={panelParam.panelHeaderHeight}
    >
       <ConfigProvider
        theme={{
          token:{
            colorPrimary: '#7F5539',
          },
          components: {
            Form: {
              labelFontSize: 15,
              itemMarginBottom: 2,
            },
            Radio: {

            }
          }
        }}
      >
        <div className="pi-view" ref={piViewRef}>
          <div className="pi-view-radio">
              <Radio.Group
                onChange={onRadioChange}
                value={radioValue}
                optionType="button"
                size="small"
              >
                <Radio.Button className="radio-button-statistics" value="Statistics">Statistics</Radio.Button>
                <Radio.Button className="radio-button-pi-list" value="PI List">List</Radio.Button>
            </Radio.Group>
            </div>
            <Spin tip="Loading" spinning={isPIViewLoading} size='large'>
            <div className="pi-statistics-subview" style={{display: isPIStatisticsDisplay}}>
              <div className="demographic-part">
                <SubTitle title="Demographic" />
                <div className="demographic-part-content">
                  <div className='chart-div radar-chart-part'>
                    <div className='gender-part' ref={radarAndDonutChartParentDivRef}>
                      <div className="demographic-title">Gender:</div>
                      <div className="demographic-donut-radar-graph">
                          <div className="legend">
                            {
                              _.map(colorMapGender, (color, attributeName) => (
                                <div key={attributeName} className="legend-item">
                                  <span className="legend-color" style={{ backgroundColor: colorMapGender[attributeName] }}></span>
                                  <span className="legend-name">{attributeName}</span>
                                </div>
                              ))
                            }
                          </div>
                          <div className="gender-donut-rader-chart" style={{ width: donutChartDiameter, height: donutChartDiameter}}>
                            <DonutAndRadarChart
                              // DonutChart
                              donutChartDataOuter={piViewGenderDonutChartDataOuter}
                              donutChartDataInner={piViewGenderDonutChartDataInner}
                              donutChartDiameter={donutChartDiameter}
                              // donutChartDiameter={containerWidth > 0 ? containerWidth * 0.35 : 200}
                              // diameter={viewParam.width * 0.35}
                              donutChartColorMap={colorMapGender}

                              // RadarChart
                              radarChartTitle="gender-rader-chart"
                              radarChartData={piViewGenderRadarChartData}
                              radarChartDiameter={radarChartDiameter}
                              // radarChartDiameter={containerWidth > 0 ? containerWidth * 0.24 : 137}
                              // diameter={viewParam.width * 0.24}
                              radarChartColorMap={colorMapGender}
                            />
                            {/* <div className="donut-chart">
                              <DonutChart
                                dataOuter={piViewGenderDonutChartDataOuter}
                                dataInner={piViewGenderDonutChartDataInner}
                                diameter={containerWidth > 0 ? containerWidth * 0.35 : 200}
                                // diameter={viewParam.width * 0.35}
                                colorMap={colorMapGender}
                              />
                            </div>
                            <div className="rader-chart">
                              <RadarChart
                                chartTitle="gender-rader-chart"
                                data={piViewGenderRadarChartData}
                                diameter={containerWidth > 0 ? containerWidth * 0.24 : 137}
                                // diameter={viewParam.width * 0.24}
                                colorMap={colorMapGender}
                              />
                            </div> */}
                           </div>
                      </div>
                    </div>
                    <div className='career-age-part'>
                      <div className="demographic-title">Career Age:</div>
                      <div className="demographic-donut-radar-graph">
                        <div className="legend">
                            {
                              _.map(colorMapCareerAge, (color, attributeName) => (
                                <div key={attributeName} className="legend-item">
                                  <span className="legend-color" style={{ backgroundColor: colorMapCareerAge[attributeName] }}></span>
                                  <span className="legend-name">{attributeName}</span>
                                </div>
                              ))
                            }
                        </div>
                        <div className="career-age-donut-rader-chart" style={{ width: donutChartDiameter, height: donutChartDiameter}}>
                        <DonutAndRadarChart
                          // DonutChart
                          donutChartDataOuter={piViewCareerAgeDonutChartDataOuter}
                          donutChartDataInner={piViewCareerAgeDonutChartDataInner}
                          donutChartDiameter={donutChartDiameter}
                          // donutChartDiameter={containerWidth > 0 ? containerWidth * 0.35 : 200}
                          // diameter={viewParam.width * 0.35}
                          donutChartColorMap={colorMapCareerAge}

                          // RadarChart
                          radarChartTitle="career-age-rader-chart"
                          radarChartData={piViewCareerAgeRadarChartData}
                          radarChartDiameter={radarChartDiameter}
                          // radarChartDiameter={containerWidth > 0 ? containerWidth * 0.24 : 137}
                          // diameter={viewParam.width * 0.24}
                          radarChartColorMap={colorMapCareerAge}
                        />
                        </div>
                        {/* <Row>
                            <div className="legend" style={{width: "160px"}}>
                              {Object.keys(colorMapCareerAge).map((attributeName) => (
                                <div key={attributeName} className="legend-item">
                                  <span className="legend-color" style={{ backgroundColor: colorMapCareerAge[attributeName] }}></span>
                                  <span className="legend-name">{attributeName}</span>
                                </div>
                              ))}
                            </div>
                          </Row>
                          <Row>
                              <div className="career-age-donut-rader-chart">
                                  <div className="donut-chart">
                                    <DonutChart
                                      dataOuter={piViewCareerAgeDonutChartDataOuter}
                                      dataInner={piViewCareerAgeDonutChartDataInner}
                                      diameter={containerWidth > 0 ? containerWidth * 0.35 : 200}
                                      // diameter={viewParam.width * 0.35}
                                      colorMap={colorMapCareerAge}
                                    />
                                  </div>
                                  <div className="rader-chart">
                                    <RadarChart
                                      chartTitle="career-age-rader-chart"
                                      data={piViewCareerAgeRadarChartData}
                                      diameter={containerWidth > 0 ? containerWidth * 0.24 : 137}
                                      // diameter={viewParam.width * 0.24}
                                      colorMap={colorMapCareerAge}
                                    />
                                  </div>
                              </div>
                          </Row> */}
                      </div>
                    </div>
                  </div>
                  <div className='chart-div'>
                    <div className="demographic-title">University:</div>
                    <div className="demographic-bar-graph">
                      {universityBarChart}
                    </div>
                  </div>
                  <div className='chart-div'>
                    <div className="demographic-title">Science_field:</div>
                    <div className="demographic-bar-graph">
                      {sciFieldBarChart}
                    </div>
                  </div>
                </div>
              </div>
              <div className="science-metric-part">
                <SubTitle title="Science Metric" />
                <div className="science-metric-part-content">
                  <div className="chart-div">
                    <div className="science-metric-title">
                      H-index:
                    </div>
                    <div className="science-metric-graph">
                      <AreaChart
                          chartTitle="H-index"
                          data={piViewHIndexAreaChartData}
                          width={barAndAreaChartWidth}
                          height={areaChartHeight}
                          color={areaChartColor}
                          drawLabel={false}
                      />
                    </div>
                  </div>
                  <div className="chart-div">
                    <div className="science-metric-title">
                      Productivity:
                    </div>
                    <div className="science-metric-graph">
                      <AreaChart
                        chartTitle="Productivity"
                        data={piViewProductivityAreaChartData}
                        width={barAndAreaChartWidth}
                        height={areaChartHeight}
                        color={areaChartColor}
                        drawLabel={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="chart-div pi-list-subview" style={{display: isPIListDisplay}}>
              <div className="card-list-container" style={{overflow:"auto"}} ref={cardListContainerRef}>
                <div className='card-list-sort'>
                  <Select
                      size="small"
                      style={{ margin: 4, width: "17vw" }} // width: 185
                      placeholder="Rank By"
                      onChange={handleSelectChange}
                      options={[
                        // { value: PICardSortMetric.PSCORE, label: PICardSortMetric.PSCORE },
                        { value: PICardSortMetric.TOTALGRANTS, label: PICardSortMetric.TOTALGRANTS },
                        { value: PICardSortMetric.TOTALPUBLICATIONS, label: PICardSortMetric.TOTALPUBLICATIONS },
                        { value: PICardSortMetric.HINDEX, label: PICardSortMetric.HINDEX },
                        { value: PICardSortMetric.PRODUCTIVITY, label: PICardSortMetric.PRODUCTIVITY },
                      ]}
                  />
                  <Select
                    size="small"
                    style={{ margin: 4, width: "17vw" }} // width: 125, marginLeft:"15px"
                    placeholder="Rank Order"
                    onChange={handleSelectOrderChange}
                    options={[
                      { value: 'ascending', label: 'ascending' },
                      { value: 'descending', label: 'descending' },
                    ]}
                  />
                  <Select
                    showSearch
                    size="small"
                    style={{ margin: 4, width: "17vw" }}  // width: 325 width: cardListWidth
                    placeholder="Select University"
                    value={selectedUniversity}
                    optionFilterProp="children"
                    onSearch={handleSelectUniversitySearch}
                    filterOption={handleSelectUniversityfilterOption}
                    onChange={handleSelectUniversityChange}
                    options={universityOptionData}
                  />
                </div>
                {/* <div className="download-button-part">
                  <DownloadButton handleDownload={handleDownload} />
                </div> */}
                <div className='card-list'>
                  {/* {researcherStatisticsCardList} */}
                  {PICardElemementList}


                  {/* opt 1: abandon (vis2024), use component `FixedSizeList` */}
                  {/* <FixedSizeList
                    itemData={sortedResearcherList}
                    itemCount={sortedResearcherList.length}
                    itemSize={PICardHeight}
                    height={450}
                    width={340}
                  >
                    {({ data, index, style}) => {
                      let currBoxShadowStyle: string = ''
                      let divOutlineColor: string = themeColor.defaultMode.selectionColor.defaultCard
                      if (selectedResearcherID === data[index].researcherID) {
                        currBoxShadowStyle = boxShadowStyle
                        divOutlineColor = themeColor.defaultMode.selectionColor.highlight
                      } else {
                        // do nothing
                      }
                      return (
                        <div style={style}>
                          <div key={index}
                            id={`${data[index].demographicInfo.fullName}-${data[index].researcherID}-pi-card`}
                            // style={{ boxShadow: boxShadowStyle }}
                            onClick={(e)=>handleOnClickPICard(e)}
                          >
                            <PICard key={index}
                              viewParam={viewSize.PIStatisticsViewCard}
                              PIStatisticsData={data[index]}
                              boxShadowStyle={currBoxShadowStyle}
                              divOutlineColor={divOutlineColor}
                            />
                          </div>
                        </div>
                      )
                    }}
                  </FixedSizeList> */}
                </div>
              </div>
            </div>
            </Spin>
          </div>
      </ConfigProvider>
    </Panel>
    </div>
  );
};

export default PIView;
